import { useMemo } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import { fetchSegmentsAt } from "../../api/queries";
import type { Segment, TimelineBlock } from "../../api/types";
import { coversMs } from "../../player/Player";
import { usePlayer } from "../../state/player";
import { nameSpanPx } from "../draw";
import { TRACK_MERGE_SPAN_MS, landsOnLiveEdge, liveEndMs, revealZoom } from "../geometry";
import type { TimelineCtx } from "./state";

/** What a landing needs on top of the shared context: the query cache it warms. */
export interface LandingCtx extends TimelineCtx {
  queryClient: QueryClient;
}

/**
 * The full row behind a timeline block, warmed into the query cache on the way past.
 *
 * Both landing gestures resolve this before they touch the player. A block carries enough
 * to draw itself and not enough to describe what is playing -- no artwork, no duration --
 * and the player looks the rest up by id, so a source handed over before this has resolved
 * is one it cannot name yet.
 */
function describe(c: LandingCtx, blockId: number): Promise<Segment> {
  return c.queryClient.fetchQuery({
    queryKey: ["segment", blockId],
    queryFn: () => api.get<Segment>(`/api/segments/${blockId}`),
  });
}

/**
 * The same warming, for an instant rather than a block.
 *
 * Not every landing has an id to look up: a bucket standing for several collapsed tracks
 * carries none, and bare archive between blocks never had one. Those become window
 * playback, which the player names by asking what covers the instant -- so the answer is
 * fetched here, before the switch, or the player has nothing to show but a placeholder for
 * the length of its own round trip.
 */
function describeAt(c: LandingCtx, ms: number) {
  return fetchSegmentsAt(c.queryClient, ms);
}

/**
 * The show covering an instant, if one does.
 *
 * Past the buffer the exported show file is the only playlist there is, so a landing out
 * there needs the show itself rather than whatever the pick happened to find: a track
 * inside a show has no export of its own to ask for, a bucket of collapsed tracks has no
 * id at all, and the pick carries a few pixels of slop besides. The instant is the one
 * handle that answers all three. Shares `describeAt`'s cached query, so a landing that has
 * already warmed it pays nothing.
 */
async function showAt(c: LandingCtx, ms: number): Promise<Segment | null> {
  const page = await describeAt(c, ms);
  return page.items.find((row) => row.kind === "show" && coversMs(row, ms)) ?? null;
}

/**
 * Zoom in until a block that has just been selected can say what it is.
 *
 * A click in the jukebox lane at a day's zoom lands on a sliver two pixels wide: the border
 * goes round it and the timeline still has not said what was selected. So the click that
 * selects an unreadable block also buys it the room for its name -- measured against the
 * caption the renderer would write rather than guessed, because that is the thing that has
 * to fit.
 *
 * Reports whether the view actually moved, which is the difference between a click that took
 * the view somewhere and one that left it where it was.
 */
function reveal(c: LandingCtx, item: TimelineBlock, anchorMs: number): boolean {
  const ctx = c.refs.canvas.current?.getContext("2d");
  if (!ctx) return false;
  const needPx = nameSpanPx(ctx, item, c.t);
  if (needPx === null) return false;
  const next = revealZoom(c.refs.view.current, {
    startMs: Date.parse(item.start),
    // The end its own lane draws it to: the show band stretches a running block to the
    // clock, and the jukebox lane leaves the present to the ghost.
    endMs: liveEndMs(item, item.kind === "show" ? Date.now() : null),
    anchorMs,
    needPx,
    // Past the merge span the jukebox lane is one section and names nothing inside it,
    // however wide the block itself is drawn.
    maxViewSpanMs: item.kind === "track" ? TRACK_MERGE_SPAN_MS : undefined,
  });
  if (!next) return false;
  c.refs.view.current = c.walls.clampView(next);
  c.refs.dirty.current = true;
  c.syncWindow();
  return true;
}

/**
 * Pin the view to the live edge, and optionally start listening to it.
 *
 * Landing a pan on the edge plays, because landing a pan anywhere already means "play from
 * here" (see `scrubTo`), and so does a click in the live delay, for the same reason a click
 * anywhere else in the data does. Wheeling into it does not, because the wheel has never
 * landed a play and turning one notch into an audio stream would be a surprise.
 *
 * Declared above `activate` only so that `activate` may depend on it.
 */
function enterLive(c: LandingCtx, andPlay: boolean): void {
  const player = usePlayer.getState();
  player.setAtLive(true); // implies follow
  if (andPlay && player.source?.kind !== "live") player.play({ kind: "live" });
  // The clock owns the bar from here; a pan's aim would only hold it back a frame.
  c.refs.aim.current = null;
  c.refs.dirty.current = true;
}

/** Commit the visible landing intent before any descriptive lookup. */
function beginLanding(c: LandingCtx, ms: number): void {
  const player = usePlayer.getState();
  player.stop();
  player.setAtLive(false);
  player.setFollow(true);
  player.setPosition(ms);
  c.refs.aim.current = null;
  c.refs.dirty.current = true;
}

/**
 * Put the playhead on an instant, and start whatever can be heard from there.
 *
 * Shared by the two landings -- a click on bare canvas, and a pan coming to rest -- which
 * differ only in how the instant was chosen and in what they had to look up first.
 *
 * The playhead is written before any source change, and written whether or not anything
 * plays. The centre bar is the playhead by construction, so an instant with no audio under
 * it still has to be the instant the bar denotes: refusing to move the playhead there does
 * not leave the bar honest, it leaves it pointing at a moment the viewer has panned away
 * from. That refusal is what kept archived shows reachable only by clicking them.
 *
 * Two ways to be audible. The buffer answers a bare instant, and past it no arbitrary
 * instant can be served at all -- `landed`, a show opened part-way through from its own
 * exported file, is the only thing left to ask for out there. A landing that finds neither
 * stops the audio, because sound continuing from somewhere the playhead no longer is would
 * make a liar of the bar.
 *
 * Neither of them re-opens anything while a session is running: the server holds the cursor,
 * so handing the player a new source is a cursor move on the body already playing
 * (player/useAudioSession). There is nothing here to make cheaper by checking coverage first.
 *
 * `landed` may also be a row that will not play at all: an expired track out past the
 * buffer is still what the bar came down on, and handing it over is what names it. The hook
 * refuses it and says so -- the same thing clicking one does. Stopping instead left the
 * player reading "Nothing playing" over a block on screen.
 */
function landOn(c: LandingCtx, ms: number, landed: Segment | null): void {
  const player = usePlayer.getState();
  // Not the edge any more, but still following: the view is slaved to the playhead, and
  // the playhead is now this instant rather than the clock.
  player.setAtLive(false);
  player.setFollow(true);
  player.setPosition(ms);
  // The aim is now the position, so the bar reads the same either way and the pan's
  // stand-in has nothing left to stand in for.
  c.refs.aim.current = null;

  if (c.walls.inArchive(ms)) {
    player.play(
      coversMs(landed, ms)
        ? { kind: "segment", segment: landed, atMs: ms }
        : { kind: "window", startMs: ms },
    );
    return;
  }
  // Past the newest instant a window can be cut over, and short of the clock. The archive
  // has nothing to hand over out here -- the bytes are still being written -- but the audio
  // exists and arrives as the live stream, so that is what playing from here means. The
  // same answer a click in the drawn band already gets (see `activate`), now that the view
  // reaches this far off live as well.
  const nowMs = Date.now();
  if (ms < nowMs && landsOnLiveEdge(ms, c.latestMs(), nowMs, c.refs.liveDelay.current)) {
    enterLive(c, true);
    return;
  }
  if (coversMs(landed, ms)) {
    // Unplayable rows are handed over here too, for the reason `activate` gives below:
    // the hook is what names the refusal, and naming it is the whole point.
    player.play({ kind: "segment", segment: landed, atMs: ms });
    return;
  }
  // Nothing was ever recorded here. Clearing the error as well as the source: a refusal
  // left over from the last thing that failed to play is not a fact about this instant.
  player.stop();
  player.setError(null);
}

/** A click: on a block, on a bucket standing for several, or on bare canvas. */
async function activate(c: LandingCtx, block: TimelineBlock | null, ms: number): Promise<void> {
  const player = usePlayer.getState();
  if (block?.id == null) {
    // The live block is one track and knows where it began (timeline/ghost), so a click on
    // it means that instant rather than the pixel under the pointer -- the same thing
    // clicking any other track means. It carries no id because the segmenter has not
    // written its row yet, which is exactly why it cannot be handed over as one: it plays
    // as a window instead, and a window rolls on through the live edge the way the block
    // itself is drawn to. `on_air` is what tells it from a bucket, which stands for several
    // tracks and so has no one start to offer.
    const at = block?.on_air ? Date.parse(block.start) : ms;
    // Bare timeline, or a bucket standing for several tracks: land on that instant rather
    // than guessing which track was meant. Anywhere on record, not merely anywhere the
    // buffer can still be cut over -- `landOn` works out what that instant can offer.
    // Off the record entirely is a different matter: the viewport reaches half its width
    // past the wall, and a click on the canvas out there is a click on nothing at all.
    if (!c.walls.onRecord(at)) {
      // Except inside the live delay. That is past the wall too, but it is drawn, it is
      // named in the key, and what it holds is the newest audio there is -- so a click on
      // it means the same thing a pan reaching for it means. Past the clock is still
      // nothing, and still does nothing.
      const nowMs = Date.now();
      if (at < nowMs && landsOnLiveEdge(at, c.latestMs(), nowMs, c.walls.wallDelayMs())) {
        enterLive(c, true);
      }
      return;
    }
    // A click is a landing like a scrub is, and both wait on a lookup: whichever was asked
    // for last is the one the player must end up pointed at. Claimed after the refusal
    // above, so a click on nothing does not cancel a landing that is still resolving.
    const token = ++c.refs.landing.current;
    beginLanding(c, at);
    let covering: Segment | null;
    try {
      covering = await showAt(c, at);
    } catch {
      if (c.refs.landing.current === token) player.setError(c.t("player.streamFailed"));
      return;
    }
    if (c.refs.landing.current !== token) return;
    landOn(c, at, covering);
    return;
  }
  const token = ++c.refs.landing.current;
  beginLanding(c, Date.parse(block.start));
  player.select(block.id);
  reveal(c, block, ms);
  let segment: Segment;
  try {
    segment = await describe(c, block.id);
  } catch {
    if (c.refs.landing.current === token) player.setError(c.t("player.streamFailed"));
    return;
  }
  if (c.refs.landing.current !== token) return;
  player.play({ kind: "segment", segment });
}

/**
 * Land a scrub: play from the instant the playhead was dragged onto, and select whatever
 * the centre bar came down on.
 *
 * The playhead is drawn at the centre of the view, so panning drags the content under it
 * and the centre is where the viewer aimed. Following resumes whatever comes of it: the
 * centre line is only honest about being the playhead while it tracks the position, and
 * `landOn` is where the instant becomes the playhead and the audio, if any, follows it.
 *
 * The landing is resolved before any of that, for the same reason clicking one is: a window
 * playlist has no name of its own, so switching to one the player cannot yet describe leaves
 * it announcing a placeholder for as long as the lookup takes. By id where the centre bar
 * came down on a block that contains the instant, and by instant otherwise -- the pick
 * carries a few pixels of slop, and a bucket of collapsed tracks has no id at all. Nothing
 * is touched until the answer is in hand, so the previous source keeps describing itself
 * meanwhile -- which is true -- instead of the new one describing nothing.
 *
 * A landing past the ends of the record is not a landing. The pan stops the centre bar on
 * the wall rather than where the gesture asked for -- see `clampToBounds` -- and the wall
 * itself is the first instant with nothing beyond it. Inside the record is a different
 * matter: a stretch whose buffer bytes are gone still holds shows that play from their own
 * files, and a hole that never held anything is still a place the playhead may sit. Only
 * the same interval the pan respects is refused here, or the bar and the view disagree
 * about where the gesture ended. Reaching the live edge is the exception `enterLive` takes.
 */
async function scrubTo(c: LandingCtx, ms: number, blockId: number | null): Promise<void> {
  if (!c.walls.onRecord(ms)) {
    // Refused, so nothing is going to make the aim true: the bar goes back to reading the
    // position rather than sitting on a centre the gesture never earned.
    c.refs.aim.current = null;
    c.refs.dirty.current = true;
    return;
  }
  // A landing supersedes any still resolving: two flicks in quick succession must not
  // let the slower lookup apply on top of the newer one.
  const token = ++c.refs.landing.current;
  beginLanding(c, ms);
  // No `select` here. The border is already honest and was before the pointer came up: the
  // render loop reads it off the bar, and the bar has been sitting on the aim since the
  // drag began. Setting it again from `blockId` would be a second authority answering the
  // same question by a different rule -- a pixel pick, slop and all, against containment of
  // the instant -- and the two disagree at exactly the boundary a scrub is aimed at.
  const landed = blockId === null ? null : await describe(c, blockId).catch(() => null);
  if (c.refs.landing.current !== token) return;

  // The landed block does not name this instant -- a bucket of collapsed tracks carries no
  // id at all, and the pick carries a few pixels of slop -- so what covers it is resolved
  // by instant instead. A seek needs that as much as a switch does: the source stays, but
  // the playhead still moves off whatever was being named.
  //
  // A show rather than whatever the pick found, because past the buffer that answer is
  // load-bearing rather than merely cosmetic: no window playlist can be cut out there, the
  // exported show file is the only playlist left, and a track inside a show has no export
  // of its own to ask for.
  let covering = coversMs(landed, ms) && landed?.kind === "show" ? landed : null;
  if (!covering) {
    try {
      covering = await showAt(c, ms);
    } catch {
      if (c.refs.landing.current === token)
        usePlayer.getState().setError(c.t("player.streamFailed"));
      return;
    }
    if (c.refs.landing.current !== token) return;
  }
  // Still nothing out here, and past the buffer `landOn` would stop the audio and leave the
  // player reading "Nothing playing" over a block the bar is sitting on. The row the pick
  // found names the instant even when no playlist will serve it, so hand it over -- the same
  // thing clicking it does (see `activate`), which is what lets an expired track caption the
  // player and grey the transport out instead of describing nothing.
  if (!covering && coversMs(landed, ms)) covering = landed;

  landOn(c, ms, covering);
}

/** The three landings the gesture layer calls, bound to a context that outlives a render. */
export interface Landing {
  enterLive: (andPlay: boolean) => void;
  activate: (block: TimelineBlock | null, ms: number) => Promise<void>;
  scrubTo: (ms: number, blockId: number | null) => Promise<void>;
}

export function useLanding(c: LandingCtx): Landing {
  return useMemo(
    () => ({
      enterLive: (andPlay: boolean) => enterLive(c, andPlay),
      activate: (block: TimelineBlock | null, ms: number) => activate(c, block, ms),
      scrubTo: (ms: number, blockId: number | null) => scrubTo(c, ms, blockId),
    }),
    [c],
  );
}
